import { queryKeys } from '@lib/queryKeys'
import type { GetChatUnreadResponse } from '@orbit/api-types'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import * as chatApi from './chatApi'
import { outboxFor } from './outbox'
import {
	advanceReadPosition,
	applyNewestPage,
	applyOlderPage,
	deleteMessage,
	finishDeletion,
	layoutStream,
	markUnread,
	needsOlderPage,
	needsOlderPageForUnread,
	quoteOf,
	type ChatQuote,
	type OutgoingMessage,
} from './core'
import { useChatUpdates } from './useChatUpdates'
import { useStreamActions } from './useStreamActions'

/** What happened to the stream, which says how the screen scrolls. */
export type ChatUpdate =
	/** The Chat opened and its pages up to the first unread Message arrived: to the divider, or the bottom. */
	| { type: 'opened' }
	/** The pages up to a Message asked for arrived: to it. Ends an opening asked to reveal one. */
	| { type: 'revealed'; messageId: number }
	/** An older page went on top: what's on screen stays where it is. */
	| { type: 'olderLoaded' }
	/** I sent a Message: to it. */
	| { type: 'sent' }
	/** Anything else: a live Message, a confirmed or failed send, a deletion. */
	| { type: 'changed' }

/** Held off after a failed older page, or every scroll near the top would ask again. */
const OLDER_RETRY_DELAY_MS = 3000
/** How far to page back for the first unread Message, or a Message to reveal, 50 at a time. */
const SEEK_PAGE_LIMIT = 10

/**
 * Drives one trip's Chat page: loads the stream, sends and deletes Messages, keeps the
 * stream live from the trip room and marks Messages read as they come on screen. The
 * state itself is the pure `ChatStream`; this wires it to the server. Ported from iOS
 * `ChatViewModel`. Mount it once per trip (key the page by trip).
 */
export const useChat = (tripId: string, myUserId: number | undefined, revealMessageId: number | null) => {
	const queryClient = useQueryClient()
	const { stream, streamRef, update, actions } = useStreamActions(tripId, outboxFor(myUserId, tripId))
	const [status, setStatus] = useState({ hasLoaded: false, loadFailed: false })
	const [lastUpdate, setLastUpdate] = useState<{ update: ChatUpdate; sequence: number }>({
		update: { type: 'changed' },
		sequence: 0,
	})

	const controller = useMemo(() => {
		let hasLoaded = false
		let isLoadingOlder = false
		let seekPagesLeft = 0
		/** The Message to scroll to once its page is loaded. */
		let revealTarget: number | null = null
		/** How far I've read; undefined until the server says, so nothing is marked on a guess. */
		let lastRead: number | null | undefined
		let marksStarted = 0
		let lastMarkApplied = 0

		const emit = (next: ChatUpdate) => setLastUpdate(prev => ({ update: next, sequence: prev.sequence + 1 }))
		const setUnread = (unread: GetChatUnreadResponse) => queryClient.setQueryData(queryKeys.chatUnread(tripId), unread)

		/**
		 * Shows the Chat once the Message sought is loaded, paging back for it first: the one
		 * to reveal, or else the first unread. Shown any earlier, the divider would sit at the
		 * top of a later page and the Messages under it would be marked read before their turn.
		 */
		const finishSeeking = () => {
			const current = streamRef.current
			const needsOlder = revealTarget !== null ? needsOlderPage(current, revealTarget) : needsOlderPageForUnread(current)
			if (needsOlder && seekPagesLeft > 0 && loadOlder(true)) {
				seekPagesLeft--
				return
			}
			if (revealTarget !== null) {
				const messageId = revealTarget
				revealTarget = null
				emit({ type: 'revealed', messageId })
			} else {
				emit({ type: 'opened' })
			}
		}

		/** Returns whether a page was asked for: not while another is on its way, nor once the oldest is loaded. */
		const loadOlder = (isSeeking: boolean): boolean => {
			const cursor = streamRef.current.olderCursor
			if (cursor === null || isLoadingOlder) return false
			isLoadingOlder = true
			chatApi.fetchMessages(tripId, cursor).then(
				page => {
					isLoadingOlder = false
					update(current => applyOlderPage(current, page.messages, page.nextBefore, cursor))
					if (isSeeking) finishSeeking()
					else emit({ type: 'olderLoaded' })
				},
				() => {
					if (isSeeking) {
						// Show what's loaded; the divider sits at its top.
						seekPagesLeft = 0
						finishSeeking()
					}
					setTimeout(() => {
						isLoadingOlder = false
					}, OLDER_RETRY_DELAY_MS)
				},
			)
			return true
		}

		const loadNewest = (isOpening: boolean) => {
			chatApi.fetchMessages(tripId).then(
				page => {
					update(current => applyNewestPage(current, page.messages, page.nextBefore))
					hasLoaded = true
					setStatus({ hasLoaded: true, loadFailed: false })
					if (isOpening) finishSeeking()
					else emit({ type: 'changed' })
				},
				() => {
					setStatus(prev => ({ ...prev, loadFailed: !hasLoaded }))
					emit({ type: 'changed' })
				},
			)
		}

		return {
			/** Places the "N new" divider from where I was, then loads the newest page — or opens on a Message. */
			open: (reveal: number | null) => {
				revealTarget = reveal
				chatApi.fetchUnread(tripId).then(
					unread => {
						update(current => markUnread(current, unread))
						lastRead = unread.lastReadMessageId
						setUnread(unread)
					},
					() => {
						// Without the position there's no divider, but reading still counts:
						// the server ignores a mark that would move it backwards.
						if (lastRead === undefined) lastRead = null
					},
				).finally(() => {
					seekPagesLeft = SEEK_PAGE_LIMIT
					loadNewest(true)
				})
			},
			loadOlder: () => {
				loadOlder(false)
			},
			/** Scrolls to a Message already in the Chat, such as the one a reply quotes, paging back for it first. */
			reveal: (messageId: number) => {
				revealTarget = messageId
				seekPagesLeft = SEEK_PAGE_LIMIT
				finishSeeking()
			},
			refresh: () => {
				if (hasLoaded) loadNewest(false)
			},
			/** The screen reports the Messages it's showing; the newest of them marks the Chat read. */
			messagesShown: (messageIds: number[]) => {
				if (lastRead === undefined || messageIds.length === 0) return
				const messageId = advanceReadPosition(lastRead, Math.max(...messageIds))
				if (messageId === null) return
				lastRead = messageId
				const sequence = ++marksStarted
				chatApi.markRead(tripId, messageId).then(
					unread => {
						// Marks apply in the order they started: an older one landing late says less.
						if (sequence <= lastMarkApplied) return
						lastMarkApplied = sequence
						setUnread(unread)
					},
					() => undefined,
				)
			},
			/** A Message of mine shows as deleted at once and comes back if the server refuses. */
			delete: (messageId: number) => {
				const deleting = deleteMessage(streamRef.current, messageId)
				if (!deleting) return
				update(() => deleting)
				emit({ type: 'changed' })
				chatApi
					.deleteMessage(tripId, messageId)
					.catch(() => null)
					.then(placeholder => {
						const finished = finishDeletion(streamRef.current, messageId, placeholder)
						update(() => finished.stream)
						if (finished.failed) toast.error("Couldn't delete the message. Please try again.")
						emit({ type: 'changed' })
					})
			},
			emit,
		}
	}, [queryClient, streamRef, tripId, update])

	const opened = useRef(false)
	useEffect(() => {
		if (opened.current) {
			// Asked to open on another Message while the Chat is already up.
			if (revealMessageId !== null) controller.reveal(revealMessageId)
			return
		}
		opened.current = true
		controller.open(revealMessageId)
	}, [controller, revealMessageId])

	useChatUpdates(tripId, controller.refresh)

	const send = useCallback(
		(message: OutgoingMessage) => {
			actions.send(message)
			controller.emit({ type: 'sent' })
		},
		[actions, controller],
	)

	const items = useMemo(() => (myUserId === undefined ? [] : layoutStream(stream, myUserId)), [stream, myUserId])

	/** What an empty stream says; null while it isn't empty or is still loading. */
	const emptyState =
		stream.messages.length > 0 || stream.pending.length > 0
			? null
			: status.loadFailed
				? { title: "Couldn't load the Chat", message: 'Check your connection and try again.' }
				: status.hasLoaded
					? { title: 'No messages yet', message: 'Say hi to the group!' }
					: null

	const chatActions = useMemo(
		() => ({
			...actions,
			send,
			loadOlder: controller.loadOlder,
			reveal: controller.reveal,
			delete: controller.delete,
			messagesShown: controller.messagesShown,
		}),
		[actions, send, controller],
	)

	/** A Message to reply to; null once it's deleted. */
	const quoteOfMessage = useCallback((messageId: number): ChatQuote | null => quoteOf(streamRef.current, messageId), [streamRef])

	return {
		stream,
		/**
		 * The rows to show. A change here with no new `lastUpdate` (a confirmed send, a
		 * reaction, a booking's people) is a plain change, as iOS's `onChange` is.
		 */
		items,
		hasLoaded: status.hasLoaded,
		emptyState,
		lastUpdate,
		quoteOf: quoteOfMessage,
		actions: chatActions,
	}
}
